import type { TFunction } from 'i18next';

/**
 * Never surface raw Axios/HTTP-client/timeout text to users (e.g. "timeout of
 * 30000ms exceeded"). This module classifies a caught error into a safe,
 * translated, user-facing message and logs the unsafe details (status, stable
 * backend error code, endpoint category, elapsed time, request/correlation
 * ids) to the console for developers only.
 */

export type ApiErrorCategory = 'timeout' | 'network' | 'server' | 'client' | 'unknown';

export interface SanitizedApiError {
  status?: number;
  code?: string;
  category: ApiErrorCategory;
  message: string;
  retryable: boolean;
}

const isTimeout = (error: any): boolean =>
  error?.code === 'ECONNABORTED' || /timeout/i.test(String(error?.message ?? ''));

const isNetworkError = (error: any): boolean => !error?.response && !isTimeout(error);

/**
 * Backend business-rule responses (403/409/etc.) that carry a stable
 * `errorCode`/`code` are deliberately authored to be safe (DEC-ACT-029); those
 * messages may be shown as-is. Anything else - unstructured 4xx bodies, 5xx
 * bodies that might contain an unhandled exception message, or no response at
 * all (network/timeout) - always falls back to a generic safe message.
 */
const structuredBackendMessage = (error: any): string | undefined => {
  const data = error?.response?.data;
  const hasStableCode = Boolean(data?.errorCode || data?.code);
  return hasStableCode && typeof data?.message === 'string' ? data.message : undefined;
};

export interface SanitizeApiErrorOptions {
  endpointCategory: string;
  t: TFunction;
}

export function sanitizeApiError(error: any, { endpointCategory, t }: SanitizeApiErrorOptions): SanitizedApiError {
  const status: number | undefined = error?.response?.status;
  const code: string | undefined = error?.response?.data?.errorCode || error?.response?.data?.code;
  const requestId = error?.response?.headers?.['x-request-id'];
  const correlationId = error?.response?.headers?.['x-correlation-id'];
  const elapsedMs = error?.config?.__dmsRequestStartedAt
    ? Date.now() - error.config.__dmsRequestStartedAt
    : undefined;

  let category: ApiErrorCategory;
  let message: string;
  let retryable = true;

  const structured = structuredBackendMessage(error);

  if (isTimeout(error)) {
    category = 'timeout';
    message = t('apiErrors.timeout', { defaultValue: 'This is taking longer than expected.' });
  } else if (isNetworkError(error)) {
    category = 'network';
    message = t('apiErrors.network', { defaultValue: 'Could not reach the server. Check your connection and try again.' });
  } else if (status === 401) {
    category = 'client';
    retryable = false;
    message = structured ?? t('apiErrors.unauthorized', { defaultValue: 'Your session has expired. Please sign in again.' });
  } else if (status === 403) {
    category = 'client';
    retryable = false;
    message = structured ?? t('apiErrors.forbidden', { defaultValue: 'You no longer have access to this item.' });
  } else if (status === 404) {
    category = 'client';
    retryable = false;
    message = structured ?? t('apiErrors.notFound', { defaultValue: 'This item could not be found.' });
  } else if (status && status >= 500) {
    category = 'server';
    message = t('apiErrors.server', { defaultValue: 'Something went wrong on our end. Please try again.' });
  } else if (status && status >= 400) {
    category = 'client';
    message = structured ?? t('apiErrors.requestFailed', { defaultValue: 'The request could not be completed.' });
  } else {
    category = 'unknown';
    message = t('apiErrors.requestFailed', { defaultValue: 'The request could not be completed.' });
  }

  // Safe diagnostics for developers only - never rendered to the user.
  console.warn('[api]', {
    endpointCategory,
    category,
    status,
    code,
    elapsedMs,
    requestId,
    correlationId,
  });

  return { status, code, category, message, retryable };
}

/** Copy for the compact "couldn't refresh" strip when previous results exist. */
export const previousResultsWarningCopy = (t: TFunction) => ({
  message: t('apiErrors.refreshFailedKeepingPrevious', { defaultValue: "Couldn't refresh. Showing previous results." }),
  tryAgain: t('apiErrors.tryAgain', { defaultValue: 'Try again' }),
});

/** Copy for the empty-state message when there are no previous results to fall back to. */
export const noResultsTimeoutCopy = (t: TFunction) => ({
  message: t('apiErrors.slowLoad', { defaultValue: 'Actions are taking longer to load.' }),
  tryAgain: t('apiErrors.tryAgain', { defaultValue: 'Try again' }),
});
